import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DoCheck,
  HostBinding,
  Input,
  KeyValueDiffers,
  inject,
} from '@angular/core';
import { AbstractControl, FormControl } from '@angular/forms';
import { TRANSLOCO_SCOPE, TranslocoModule } from '@jsverse/transloco';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

@Component({
  selector: 'boels-form-errors',
  templateUrl: './form-errors.component.html',
  styleUrls: ['./form-errors.component.scss'],
  imports: [TranslocoModule, AsyncPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: TRANSLOCO_SCOPE,
      useValue: ['form'],
    },
  ],
})
export class FormErrorsComponent implements DoCheck {
  protected ChangeDetectionRef = inject(ChangeDetectorRef);
  protected keyValueDiffers = inject(KeyValueDiffers);

  _control: FormControl | AbstractControl;

  errorsDetails$: Observable<Array<[string, string | boolean]>>;

  @Input() prefix = 'form.errors';

  @Input()
  translationParams: { [key: string]: string | null };

  @Input() hide = false;

  /** Could be transformed to string[] if there are other exception types. */
  @Input() customError: string;

  @Input()
  set control(control: AbstractControl | FormControl | null) {
    if (!control) {
      return;
    }

    this._control = control;

    this.errorsDetails$ = control?.statusChanges.pipe(
      startWith({}),
      map(() => control.errors || {}),
      map((errors) => Object.entries(errors).filter(([, details]) => details)),
    );
  }

  get control(): FormControl | AbstractControl {
    return this._control;
  }

  ngDoCheck(): void {
    if (this.control.touched) {
      this.ChangeDetectionRef.markForCheck();
    }
  }

  getTranslationParams(errorDetails?: string | boolean): object {
    return { errorDetails, ...this.translationParams };
  }

  @HostBinding('class.control-invalid') get invalid() {
    return this.control?.invalid;
  }

  @HostBinding('class.control-dirty') get dirty() {
    return this.control?.dirty;
  }

  @HostBinding('class.control-touched') get touched() {
    return this.control?.touched;
  }

  @HostBinding('class.boels-visually-hidden') get hidden() {
    return this.hide || !(this.invalid && (this.touched || this.dirty));
  }

  @HostBinding('attr.role') role = 'alert';
}
